import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import {
  CATALIST_MATCHING_CONFIG,
  GCP_WORKFLOW_EXECUTION_METADATA_FIELDS,
  GCP_WORKFLOW_IDS,
  SNACKBAR_VARIANTS,
  TARGETSMART_MATCHING_CONFIG,
  VENDORS,
  VENDOR_NAMES,
  VENDOR_SLUGS,
} from "constants";
import BigQueryDatasetSelector from "components/bigquery/BigQueryDatasetSelector";
import BigQueryTableSelector from "components/bigquery/BigQueryTableSelector";
import FieldMatcher from "components/FieldMatcher";
import InlineMessage from "components/InlineMessage";
import PageHeader from "components/PageHeader";
import SyncNameInput from "components/SyncNameInput";
import SyncWizard from "components/SyncWizard";
import SyncWizardFooterWrapper from "components/SyncWizardFooterWrapper";
import useAsync from "hooks/useAsync";
import buildRoute from "../components/buildRoute";
import getCurrentUser from "hooks/getCurrentUser";
import getConfig from "hooks/getConfig";
import { trackEvent, trackEngagementActivity } from "../configureMixpanel";


const matchingConfigByVendor = {
  [VENDORS.CATALIST]: CATALIST_MATCHING_CONFIG,
  [VENDORS.TARGETSMART]: TARGETSMART_MATCHING_CONFIG,
};

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export function NewMatchingJobPage() {
  const [jobName, setJobName] = useState("");
  const [isJobNameValid, setIsJobNameValid] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [sourceDataset, setSourceDataset] = useState(null);
  const [sourceTable, setSourceTable] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [didUserClickNext, setDidUserClickNext] = useState(false);
  const [didUserClickSubmit, setDidUserClickSubmit] = useState(false);
  const [matchedFields, setMatchedFields] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isConnectedSheet, setIsConnectedSheet] = useState(false);


  const { projectId } = useParams();
  const loggedInUser = getCurrentUser()
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const csrftoken = getCookie('csrftoken');
  const activePartner = loggedInUser.partners.filter(partner => partner.gcp_project_id === projectId)[0]

  useEffect(() => {
    trackEngagementActivity("voter_file_matching_opened");
  }, []);

  // Use project-specific feature flags from loader, fall back to global FLAGS
  const { activeProjectDetails } = useRouteLoaderData("projectRoot");
  const projectFlags = activeProjectDetails?.featureFlags || {};
  const { FLAGS: globalFlags } = getConfig();
  const FLAGS = Object.keys(projectFlags).length > 0 ? projectFlags : globalFlags;

  const targetsmartMatchingEnabled = FLAGS?.TARGETSMART_MATCHING;
  const catalistMatchingEnabled = FLAGS?.CATALIST_MATCHING;
  const areBothMatchingProvidersEnabled = targetsmartMatchingEnabled && catalistMatchingEnabled;
  const matchingFAQsLink = targetsmartMatchingEnabled ? TARGETSMART_MATCHING_CONFIG.faqsLink : CATALIST_MATCHING_CONFIG.faqsLink;

  let subtitle = "Match your data to Catalist voter file data";
  let Step1_SelectData_Label = "Select data source";
  let Step2_MatchColumns_Label = "Match columns to Catalist fields";


  if (targetsmartMatchingEnabled) {
    subtitle = "Match your data to TargetSmart voter file data";
    Step2_MatchColumns_Label = "Match columns to TargetSmart fields";
  }

  if (areBothMatchingProvidersEnabled) {
    subtitle =
      "Match your data to voter file data from Catalist and TargetSmart";
    Step1_SelectData_Label = "Select data source & provider";
    Step2_MatchColumns_Label = "Match columns to provider fields";
  }

  const isFirstPageValid =
    isJobNameValid && Boolean(sourceDataset) && Boolean(sourceTable);

  const requiredProviderFields = useMemo(
    () => matchingConfigByVendor[selectedProvider]?.visibleFields || [],
    [selectedProvider],
  );

  const areAllRequiredFieldsMatched = useMemo(
    () =>
      requiredProviderFields.every(
        ({ header, isRequired }) =>
          !isRequired || Boolean(matchedFields[header]),
      ),
    [requiredProviderFields, matchedFields],
  );

  const columnMatchingHelperText =
    matchingConfigByVendor[selectedProvider]?.columnMatchingHelperText;

  const handleNext = () => {
    setDidUserClickNext(true);

    if (!isFirstPageValid) {
      return;
    }

    // Increment the active step, advancing users to the next page in the matching job wizard (see components/SyncWizard.jsx)
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    setDidUserClickSubmit(true);

    if (!areAllRequiredFieldsMatched) {
      return;
    }

    requestToSubmitNewMatchingExecution.perform();
  };

  const handleJobNameChange = (value) => {
    setJobName(value);
  };

  const handleIsJobNameValidChange = (value) => {
    setIsJobNameValid(value);
  };

  const handleSelectDataset = (event, option) => {
    setSourceTable(null);
    setSourceDataset(option ?? null);
  };

  const handleSelectTable = async (event, option) => {
    try {
      const tableQueryParams = new URLSearchParams();
      tableQueryParams.set("project_id", projectId);
      tableQueryParams.set("dataset_id", sourceDataset);
      tableQueryParams.set("table_id", option);
      const tableResult = await fetch(`/bigquery/get-table/?${tableQueryParams.toString()}`);
      const table = await tableResult.json();

      if (table.externalDataConfiguration?.sourceFormat === "GOOGLE_SHEETS") {
        setIsConnectedSheet(true);
        setSourceTable(null)
      } else {
        setIsConnectedSheet(false);
        setSourceTable(option);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const resetStepTwo = () => {
    setDidUserClickSubmit(false);
    setMatchedFields({});
  };

  /**
   * Reset the matching fields state when the user changes providers
   */
  const handleChangeProvider = (event, newProvider) => {
    setSelectedProvider(newProvider);
    resetStepTwo();
  };

  useEffect(() => {
    let initialSelectedProvider = VENDORS.CATALIST;

    if (
      targetsmartMatchingEnabled &&
      !catalistMatchingEnabled
    ) {
      initialSelectedProvider = VENDORS.TARGETSMART;
    }

    setSelectedProvider(initialSelectedProvider);
  }, []);

  useEffect(() => {
    requestToFetchAvailableColumns.perform();
  }, [sourceTable]);

  const requestToFetchAvailableColumns = useAsync(async () => {
    if (!sourceTable) {
      return [];
    }

    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", sourceDataset);
    queryParams.set("table_id", sourceTable);
    const response = await fetch(`/bigquery/get-table-schema/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const table_schema = await response.json();
    const column_names = table_schema.map(({ name }) => name);
    // exclude any columns beginning with an underscore
    const filtered_column_names = column_names.filter((column_name) => !column_name.startsWith("_"));
    setAvailableColumns(filtered_column_names);
    return filtered_column_names;
  });

   const requestToSubmitNewMatchingExecution = useAsync(async () => {
     // const authedRequestConfig = await getAuthedFetchConfig();
     const matchStartTime = Date.now();

     const fields = Object.entries(matchedFields)
       .filter(([_, mappedColumn]) => Boolean(mappedColumn))
       .map(([fieldName, mappedColumn]) => ({ fieldName, mappedColumn }));

    try {
      console.log("Fields:", fields);
      const newExecution = await fetch('/matching/api/trigger-workflow-execution/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({
          project_id: projectId,
          workflow_id: 'voterfile-matching',
          request_body: {
            "fields": fields,
            "project_id": projectId,
            "workflow_id": GCP_WORKFLOW_IDS.VOTERFILE_MATCHING,
            "job_name": jobName,
            "source_dataset": sourceDataset,
            "source_table": sourceTable,
            "vendor": VENDOR_SLUGS[selectedProvider],
            "metadata": {
              [GCP_WORKFLOW_EXECUTION_METADATA_FIELDS.VENDOR_ID]: selectedProvider,
            }
          }
        }),
        mode: 'same-origin'
      })

      if (!newExecution.ok) {
        const errorBody = await newExecution.json();
        throw new Error(`Failed to trigger workflow execution: ${JSON.stringify(errorBody)}`);
      }

      const createdExecution = await newExecution.json();
      console.log("Workflow execution:", createdExecution);

      enqueueSnackbar(`Workflow execution created successfully`, {
      variant: SNACKBAR_VARIANTS.SUCCESS,
      });

      // mirroring the way we build job_id in the workflow
      // since we won't have access to it at this stage of the execution
      const projectNumber = activePartner.gcp_project_number?.split('/')[1]
      const dateString = createdExecution.start_time.split('T')[0].replaceAll("-", "")
      const executionSubstring = createdExecution.name?.split('/').pop().substring(0,8)
      const jobId = `${projectNumber}_${dateString}_${executionSubstring}`

      // GCP returns the execution argument as a JSON string; parse it to an object
      // so the Django JSONField stores it correctly
      const argumentsObj = typeof createdExecution.argument === 'string'
        ? JSON.parse(createdExecution.argument)
        : (createdExecution.argument || {});

      // create new Job instance for workflow execution
      const newJob = await fetch('/matching/api/create-matching-job/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({
          partner: activePartner.id,
          vendor_name: VENDOR_SLUGS[selectedProvider],
          job_name: jobName,
          job_id: jobId,
          start_time: createdExecution.start_time,
          end_time: null,
          state: createdExecution.state,
          call_log_level: createdExecution.call_log_level,
          arguments: argumentsObj,
          result: createdExecution.result,
          error: createdExecution.error,
        }),
        mode: 'same-origin'
      })

      if (!newJob.ok) {
        const errorBody = await newJob.json();
        throw new Error(`Failed to create matching job record: ${JSON.stringify(errorBody)}`);
      }

      const createdJob = await newJob.json();
      console.log("Job Instance:", createdJob);

      trackEvent("voter_file_matching_used", {
        match_action: "start_match",
        matching_success: true,
        time_to_match_seconds: (Date.now() - matchStartTime) / 1000,
      });

      navigate(`/projects/${projectId}/matching-jobs`);

      return createdJob;
    } catch (error) {
      trackEvent("voter_file_matching_used", {
        match_action: "start_match",
        matching_success: false,
        time_to_match_seconds: (Date.now() - matchStartTime) / 1000,
      });
      throw error;
    }
   });

  const datasetsFilter = (dataset) => {
    return !dataset.toLowerCase().includes("targetsmart") &&
           !dataset.toLowerCase().includes("catalist");
  };

  const Step1_SelectData = (
    <>
      <SyncNameInput
        syncName={jobName}
        onSyncNameChange={handleJobNameChange}
        isSyncNameValid={isJobNameValid}
        onIsSyncNameValidChange={handleIsJobNameValidChange}
        showAllErrors={didUserClickNext}
      />
      
      <BigQueryDatasetSelector
        selectedDataset={sourceDataset}
        onSelectDataset={handleSelectDataset}
        showError={didUserClickNext}
        label="Select dataset"
        title="Choose Data Source"
        datasetsFilter={datasetsFilter}
      />      
      {sourceDataset && (
        <BigQueryTableSelector
          datasetId={sourceDataset}
          selectedTable={sourceTable}
          onSelectTable={handleSelectTable}
          showError={didUserClickNext}
          label="Select table"
        />
      )}
      {isConnectedSheet && (
        <InlineMessage
          type="warning"
          message={<>Tables sourced from Google Sheets are not supported for matching. Please select a different table or visit <a href={matchingFAQsLink} target="_blank" rel="noopener noreferrer">Matching FAQs</a> for information on how to transfer that data to a standard BigQuery table.</>}
          sxOverrides={{ mb: 2 }}
        />)}
      {areBothMatchingProvidersEnabled && (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Choose Provider
          </Typography>
          <ToggleButtonGroup
            fullWidth={true}
            sx={{ mb: 3 }}
            color="primary"
            value={selectedProvider}
            exclusive
            onChange={handleChangeProvider}
            aria-label="Provider"
          >
            <ToggleButton value={VENDORS.CATALIST}>Catalist</ToggleButton>
            <ToggleButton value={VENDORS.TARGETSMART}>TargetSmart</ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "end" }}>
        <Button variant="outlined" onClick={handleNext}>
          Next: Match Columns
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  const Step2_MatchColumns = (
    <>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Match columns
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Select columns from <code>{sourceTable ?? "table"}</code> to match to
          each required {VENDOR_NAMES[selectedProvider]} field
        </Typography>
        {columnMatchingHelperText && (
          <InlineMessage
            type="info"
            message={columnMatchingHelperText}
            sxOverrides={{ mb: 2 }}
          />
        )}
        {<FieldMatcher
          allFields={requiredProviderFields}
          matchedFields={matchedFields}
          matchOptions={availableColumns}
          fieldValueKey="header"
          headerLabels={[
            "Field",
            `${VENDOR_NAMES[selectedProvider]} header`,
            "Column",
          ]}
          onMatchChange={(fieldValue, newMatch) =>
            setMatchedFields({ ...matchedFields, [fieldValue]: newMatch })
          }
          areMatchOptionsLoading={requestToFetchAvailableColumns.isRunning}
          showAllErrors={didUserClickSubmit}
        /> }
      </Box>
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "between" }}>
        {requestToSubmitNewMatchingExecution.didFail && (
          <InlineMessage
            type="error"
            message={requestToSubmitNewMatchingExecution.error.message}
            sx={{ justifySelf: "start" }}
          />
        )}
        <Button color="inherit" onClick={handleBack} sx={{ mr: 1, ml: "auto" }}>
          Back
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          {`Submit ${VENDOR_NAMES[selectedProvider]} Matching Job`}
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <PageHeader
        title="Create a new voter file matching job"
        subtitle={subtitle}
      />
      <SyncWizard 
        steps={[
          {
            label: Step1_SelectData_Label,
            content: Step1_SelectData,
          },
          {
            label: Step2_MatchColumns_Label,
            content: Step2_MatchColumns,
          },
        ]}
        activeStepIndex={activeStep}
        isLoading={requestToSubmitNewMatchingExecution.isRunning}
        loadingMessage={"Submitting matching job..."}
        loadingBackdropTestId={"submit-matching-job-backdrop"}
      />
    </Box>
  );
}

const routeArgs = {
  element: <NewMatchingJobPage />,
};

export default buildRoute(routeArgs);
